const SAFE_ANALYTICS_QUERY_PARAMS = new Set([
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_content",
	"utm_term",
	"src",
	"src_slug",
	"mode",
	"goal",
]);

const EMAIL_PATTERN = /\b[^\s@]+@[^\s@]+\.[^\s@]+\b/i;
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i;
const PHONE_LIKE_PATTERN = /^\s*\+?[\d\s()./-]{7,}\s*$/;
const ORDER_NUMBER_PATTERN = /^\s*#?\d{4,}\s*$/;
const TOKEN_LIKE_PATTERN = /^\s*[A-Za-z0-9+/=_-]{24,}\s*$/;

// `stage` is also used as a number by recovery events. The project's inferred
// numeric property makes string checkout stages unreadable in HogQL. Keep the
// historical property, and provide an event-specific string field for new data.
const INFORMATION_FAILURE_STAGES = new Set([
	"email_update",
	"shipping_address_update",
	"delivery_method_update",
]);

export interface PostHogEventLike {
	event?: string;
	properties?: Record<string, unknown>;
}

export function containsSensitiveSearchValue(value: string): boolean {
	return (
		EMAIL_PATTERN.test(value) ||
		UUID_PATTERN.test(value) ||
		PHONE_LIKE_PATTERN.test(value) ||
		ORDER_NUMBER_PATTERN.test(value) ||
		TOKEN_LIKE_PATTERN.test(value)
	);
}

/** Keep route-level attribution while removing checkout/order/token/search parameters. */
export function sanitizePostHogUrl(value: string): string {
	const absolute = /^[a-z][a-z\d+.-]*:\/\//i.test(value);
	try {
		const url = new URL(value, "https://posthog-sanitizer.invalid");
		for (const key of [...url.searchParams.keys()]) {
			const values = url.searchParams.getAll(key);
			if (!SAFE_ANALYTICS_QUERY_PARAMS.has(key.toLowerCase()) || values.some(containsSensitiveSearchValue)) {
				url.searchParams.delete(key);
			}
		}
		url.hash = "";
		return absolute ? url.toString() : `${url.pathname}${url.search}`;
	} catch {
		return value.split(/[?#]/, 1)[0] ?? "";
	}
}

function sanitizeSearchProperty(properties: Record<string, unknown>, key: string): void {
	const value = properties[key];
	if (typeof value !== "string") return;
	const trimmed = value.trim();
	properties[`${key}_length`] = trimmed.length;
	if (containsSensitiveSearchValue(trimmed)) {
		properties[key] = "[redacted]";
		properties[`${key}_redacted`] = true;
		return;
	}
	if (trimmed.length > 160) {
		properties[key] = trimmed.slice(0, 160);
		properties[`${key}_truncated`] = true;
	}
}

/** expectedProjectToken must come from SDK configuration, never event data. */
export function sanitizePostHogEvent<T extends PostHogEventLike>(event: T, expectedProjectToken?: string): T {
	if (!event.properties) return event;
	const properties = redactRecoveryTelemetry(event.properties) as Record<string, unknown>;
	// The SDK uses this exact top-level property as its public ingestion key.
	// Restore only the configured key; all other token fields stay redacted.
	if (expectedProjectToken && event.properties.token === expectedProjectToken) {
		properties.token = expectedProjectToken;
	}

	for (const key of ["$current_url", "$referrer", "$initial_current_url", "$initial_referrer"]) {
		const value = properties[key];
		if (typeof value === "string") properties[key] = sanitizePostHogUrl(value);
	}

	if (event.event?.startsWith("search_")) {
		for (const key of ["query", "suggestion", "raw_query"]) {
			sanitizeSearchProperty(properties, key);
		}
	}

	if (event.event === "checkout_information_submit_failed") {
		const stage = properties.stage;
		if (typeof stage === "string" && INFORMATION_FAILURE_STAGES.has(stage)) {
			properties.information_failure_stage = stage;
		} else {
			delete properties.information_failure_stage;
		}
	}

	return { ...event, properties };
}

/** Defense in depth for nested exception values and accidental request-body properties. */
function redactRecoveryTelemetry(value: unknown, depth = 0): unknown {
	if (depth > 20) return "[redacted]";
	if (typeof value === "string") return value.replace(/\br1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, "[redacted]");
	if (Array.isArray(value)) return value.map((item) => redactRecoveryTelemetry(item, depth + 1));
	if (value && typeof value === "object")
		return Object.fromEntries(
			Object.entries(value).map(([key, item]) => [
				key,
				/^(?:recovery_?token|token)$/i.test(key) ? "[redacted]" : redactRecoveryTelemetry(item, depth + 1),
			]),
		);
	return value;
}
